"use client";

import { useEffect } from "react";
import { trackEvent } from "@/lib/analytics";

const DEDUPE_MS = 2000;
const TARGET_MAX_LENGTH = 500;
const TEXT_MAX_LENGTH = 100;

let lastClick: { target: string; at: number } | null = null;

/**
 * Site-wide click tracking: capture-phase listener that records link clicks
 * (internal and outbound) with the link target and trimmed label text.
 */
export function ClickTracker() {
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      const element = event.target instanceof Element ? event.target : null;
      const anchor = element?.closest("a[href]");
      if (!(anchor instanceof HTMLAnchorElement)) return;
      const raw = anchor.getAttribute("href") ?? "";
      if (!raw || raw.startsWith("#")) return;

      let target: string;
      let outbound: boolean;
      if (raw.startsWith("mailto:") || raw.startsWith("tel:")) {
        target = raw.slice(0, raw.indexOf(":"));
        outbound = true;
      } else {
        try {
          const url = new URL(anchor.href);
          target = `${url.host}${url.pathname}`;
          outbound = url.host !== window.location.host;
        } catch {
          return;
        }
      }
      target = target.slice(0, TARGET_MAX_LENGTH);

      const now = Date.now();
      if (lastClick && lastClick.target === target && now - lastClick.at < DEDUPE_MS) return;
      lastClick = { target, at: now };

      trackEvent("link_click", {
        path: window.location.pathname,
        target,
        text: (anchor.textContent ?? "").trim().slice(0, TEXT_MAX_LENGTH),
        outbound,
      });
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);
  return null;
}
